import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // Handle Stripe — poll up to 15 times (30 seconds total)
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      try {
        const res = await api.get(`/stripe/checkout-status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
        } else if (res.data.status === "expired") {
          setStatus("expired");
        } else if (attempts < 15) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        if (attempts < 15) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };
    poll();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-coral mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-heading text-stone-900 mb-2">Verifying your payment...</h1>
            <p className="text-stone-500 text-sm font-ui">This usually takes a few seconds. Please don't close this page.</p>
            <div className="mt-4 w-48 h-1.5 bg-stone-200 rounded-full mx-auto overflow-hidden">
              <div className="h-full bg-coral rounded-full animate-pulse" style={{ width: `${Math.min((attempts / 15) * 100, 95)}%`, transition: 'width 1s ease' }} />
            </div>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-heading text-stone-900 mb-2">Payment successful!</h1>
            <p className="text-stone-500 text-sm font-ui mb-6">Your booking has been confirmed. The lender will be notified.</p>
            <Link to="/dashboard" className="inline-flex px-6 py-2.5 bg-coral text-white rounded-full text-sm font-medium hover:bg-[#d44814] transition-colors font-ui" data-testid="go-to-dashboard">
              Go to Dashboard
            </Link>
          </>
        )}
        {status === "cancelled" && (
          <>
            <XCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h1 className="text-2xl font-heading text-stone-900 mb-2">Payment cancelled</h1>
            <p className="text-stone-500 text-sm font-ui mb-6">You cancelled the payment. Your booking was not confirmed.</p>
            <Link to="/dashboard" className="inline-flex px-6 py-2.5 bg-coral text-white rounded-full text-sm font-medium hover:bg-[#d44814] transition-colors font-ui">
              Go to Dashboard
            </Link>
          </>
        )}
        {(status === "error" || status === "expired" || status === "timeout") && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-heading text-stone-900 mb-2">
              {status === "expired" ? "Session expired" : "Something went wrong"}
            </h1>
            <p className="text-stone-500 text-sm font-ui mb-6">
              {status === "expired" ? "Your payment session has expired. Please try again." : "We couldn't confirm your payment. Please check your dashboard or contact support."}
            </p>
            <Link to="/dashboard" className="inline-flex px-6 py-2.5 bg-coral text-white rounded-full text-sm font-medium hover:bg-[#d44814] transition-colors font-ui">
              Go to Dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
